import { useState } from 'react';
import useSWR from 'swr';
import lodash from 'lodash';
import useSWRMutation from 'swr/mutation';
import { enqueueSnackbar } from 'notistack';
import {
  type ClientSettingsUpdate,
  type ClientSettings,
  type AccountCreate,
  type AccountUpdate,
  type AccountImport,
} from '@gorilla/portal-shared/src/schemas';
import { monday } from '../services/monday';
import { extractErrorMessage } from '../lib/utils';
import {
  getClient,
  updateClientSettings,
  addAccount,
  exportAccount,
  updateAccount,
  deleteAccount,
  importAccount,
} from '../services/portal';

type ClientInitState = { state: 'init' };
type ClientReadyState = { state: 'ready' };
type ClientErrorState = {
  state: 'error';
  errorKind: 'MONDAY_CONTEXT' | 'CLIENT_SERVER';
  error: string;
};
type ClientState = ClientInitState | ClientReadyState | ClientErrorState;

export type MondayContext = {
  boardId: number;
  itemId: number;
  user: {
    id: string;
  };
  permissions: {
    approvedScopes: string[];
    requiredScopes: string[];
  };
};

export function useClient() {
  const [settingsDraft, setSettingsDraft] = useState<ClientSettings | null>(null);

  const { data: mondayContext, error: mondayContextError } = useSWR(
    `/monday-context`,
    async () => {
      const mContext = ((await monday.get('context')) as any).data as MondayContext;

      if (!mContext) {
        throw new Error('This app can only be used inside of monday.com');
      }

      return mContext;
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  const {
    data: clientServer,
    mutate: fetchClient,
    error: clientServerError,
    isLoading: isClientLoading,
    isValidating: isClientValidating,
  } = useSWR(
    mondayContext?.itemId ? `/client/${mondayContext?.itemId}` : null,
    async () => {
      return await getClient(mondayContext!.boardId, mondayContext!.itemId);
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  const clientSettingsServer = clientServer?.settings;
  const slots = clientServer?.slots;

  const {
    trigger: triggerUpdateClientSettings,
    isMutating: isUpdatingClientSettings,
    error: updateClientSettingsError,
  } = useSWRMutation('/save-client-settings', async (_, { arg: newSettings }: { arg: ClientSettingsUpdate }) => {
    try {
      return await updateClientSettings(mondayContext!.boardId, mondayContext!.itemId, newSettings);
    } catch (err) {
      enqueueSnackbar(extractErrorMessage(err), { variant: 'error' });
      throw err;
    }
  });

  const { trigger: triggerAddAccount, isMutating: isAddingAccount } = useSWRMutation(
    '/add-account',
    async (_, { arg: account }: { arg: AccountCreate & { overwriteExisting: boolean } }) => {
      try {
        return await addAccount(
          mondayContext!.boardId,
          mondayContext!.itemId,
          lodash.omit(account, 'overwriteExisting') as AccountCreate,
          account.overwriteExisting,
        );
      } catch (err) {
        enqueueSnackbar(extractErrorMessage(err), { variant: 'error' });
        throw err;
      }
    },
  );

  const { trigger: triggerImportAccount, isMutating: isImportingAccount } = useSWRMutation(
    '/import-account',
    async (_, { arg: account }: { arg: AccountImport }) => {
      try {
        const res = await importAccount(mondayContext!.boardId, mondayContext!.itemId, account);
        enqueueSnackbar(`${account.email} imported successfully`, { variant: 'success' });
        return res;
      } catch (err) {
        enqueueSnackbar(extractErrorMessage(err), { variant: 'error' });
        throw err;
      }
    },
  );

  const { trigger: triggerUpdateAccount, isMutating: isUpdatingAccount } = useSWRMutation(
    '/update-account',
    async (_, { arg: { accountId, accountUpdate } }: { arg: { accountId: string; accountUpdate: AccountUpdate } }) => {
      try {
        return await updateAccount(mondayContext!.boardId, mondayContext!.itemId, accountId, accountUpdate);
      } catch (err) {
        enqueueSnackbar(extractErrorMessage(err), { variant: 'error' });
        throw err;
      }
    },
  );

  const { trigger: triggerExport, isMutating: isExporting } = useSWRMutation(
    '/export-account',
    async (_, { arg: accountId }: { arg: string }) => {
      try {
        const accountToExport = await exportAccount(mondayContext!.boardId, mondayContext!.itemId, accountId);
        const email = accountToExport?.email!;
        const emailFilename = email.replace(/[^a-z0-9]/gi, '_').toLowerCase() + '_data.json';
        const element = document.createElement('a');
        const file = new Blob([JSON.stringify(accountToExport, null, 2)], { type: 'application/json' });
        element.href = URL.createObjectURL(file);
        element.download = emailFilename;
        document.body.appendChild(element); // Required for this to work in FireFox
        element.click();
        URL.revokeObjectURL(element.href);
        document.body.removeChild(element);
      } catch (err) {
        enqueueSnackbar(extractErrorMessage(err), { variant: 'error' });
        throw err;
      }
    },
  );

  const {
    trigger: triggerDeleteAccount,
    isMutating: isDeletingAccount,
    error: deleteError,
  } = useSWRMutation('/delete-account', async (_, { arg: accountId }: { arg: string }) => {
    try {
      return await deleteAccount(mondayContext!.boardId, mondayContext!.itemId, accountId);
    } catch (err) {
      enqueueSnackbar(extractErrorMessage(err), { variant: 'error' });
      throw err;
    }
  });

  const isLoading = isClientLoading || isUpdatingClientSettings || isAddingAccount || isUpdatingAccount || isDeletingAccount;
  const clientSettings = settingsDraft || clientSettingsServer;

  if (Array.isArray(slots)) {
    // check if there are deleted slots and add them
    const slotIds = slots.map((slot) => slot.slotId);
    const deletedSlotIds = Object.keys(clientSettings?.slotSettings || {}).filter((slotId) => !slotIds.includes(slotId));

    for (const deletedSlotId of deletedSlotIds) {
      slots.push({
        slotId: deletedSlotId,
        //label: `Slot ${deletedSlotId} (Deleted)`,
        label: 'Deleted',
        description: '',
        // @ts-ignore
        isDeleted: true,
      });
    }
  }

  let clientState: ClientState = {
    state: 'init',
  };

  if (mondayContextError) {
    clientState = {
      state: 'error',
      errorKind: 'MONDAY_CONTEXT',
      error: extractErrorMessage(mondayContextError, 'An error occurred while fetching monday context'),
    };
  } else if (clientServerError) {
    clientState = {
      state: 'error',
      errorKind: 'CLIENT_SERVER',
      error: extractErrorMessage(clientServerError, 'An error occurred while fetching client data'),
    };
  }

  if (clientState.state !== 'error' && clientServer && mondayContext) {
    clientState = {
      state: 'ready',
    };
  }

  return {
    mondayContext,
    mondayContextError,
    isClientLoading,
    isClientValidating,
    clientSettings,
    clientSettingsServer,
    clientServerError,
    triggerUpdateClientSettings,
    isUpdatingClientSettings,
    triggerAddAccount,
    isAddingAccount,
    triggerUpdateAccount,
    isUpdatingAccount,
    triggerExport,
    isExporting,
    triggerDeleteAccount,
    isDeletingAccount,
    fetchClient,
    setSettingsDraft,
    settingsDraft,
    isLoading,
    deleteError,
    slots,
    updateClientSettingsError,
    clientState,
    triggerImportAccount,
    isImportingAccount,
  };
}
